import { AssetDetail, ApprovalStep } from '../../../types'
import { CheckCircle2, XCircle, Clock, User } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { Typogaphy } from 'vnetwork-ui'
import {
    approveAssetByManager,
    approveAssetByBOD,
    rejectAssetByManager,
    rejectAssetByBOD,
} from '../../../services'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { StatusPending, StatusProcessing } from '@/modules/Asset/constants'
import toast from 'react-hot-toast'
import { AxiosError } from 'axios'

interface ApprovalTabProps {
    asset: AssetDetail
}

const ApprovalStatusCard: React.FC<{
    requestStatus: string
    step: ApprovalStep
    assetVersionId: string
    onSuccess: () => void
    t: (key: string) => string
    stepNumber: number
}> = ({ requestStatus, step, assetVersionId, onSuccess, t, stepNumber }) => {
    const getApproveFunction = () => {
        if (step.approver_role === 'MANAGER') {
            return approveAssetByManager
        } else if (step.approver_role === 'BOD') {
            return approveAssetByBOD
        }
        return approveAssetByManager
    }

    const getRejectFunction = () => {
        if (step.approver_role === 'MANAGER') {
            return rejectAssetByManager
        } else if (step.approver_role === 'BOD') {
            return rejectAssetByBOD
        }
        return rejectAssetByManager
    }

    const [feedback, setFeedback] = useState('')

    const isApproved = step.status === 'APPROVED'
    const isRejected = step.status === 'REJECTED'
    const isProcessing =
        requestStatus === StatusProcessing && step.status === StatusProcessing

    const handleMutationError = (error: unknown) => {
        const status = (error as AxiosError)?.response?.status
        if (status === 403) {
            toast.error(t('permission_denied'))
        } else if (status === 404) {
            toast.error(t('not_found'))
        } else {
            toast.error(t('something_went_wrong'))
        }
    }

    const { mutateAsync: approve, isPending: isApproving } =
        useMutationWithHandler(
            { mutationFn: getApproveFunction() },
            {
                showErrorToast: false,
                successMessage: t('messages.approveProcessing'),
                reactQueryKeys: [QUERY_KEYS.ASSET_DETAIL, QUERY_KEYS.ASSETS],
                onError: handleMutationError,
            }
        )

    const { mutateAsync: reject, isPending: isRejecting } =
        useMutationWithHandler(
            { mutationFn: getRejectFunction() },
            {
                showErrorToast: false,
                successMessage: t('messages.rejectProcessing'),
                reactQueryKeys: [QUERY_KEYS.ASSET_DETAIL, QUERY_KEYS.ASSETS],
                onError: handleMutationError,
            }
        )

    const handleApprove = async () => {
        const result = await approve({
            asset_version_id: assetVersionId,
            comment: feedback.trim() || 'Approved',
        })
        if (!result) return
        setFeedback('')
        onSuccess()
    }

    const handleReject = async () => {
        if (!feedback || feedback.trim().length < 10) {
            toast.error('Need to provide a reason')
            return
        }
        const result = await reject({
            asset_version_id: assetVersionId,
            comment: feedback,
        })
        if (!result) return
        setFeedback('')
        onSuccess()
    }

    const getBorderColor = () => {
        if (isApproved) return 'border-l-green-500'
        if (isRejected) return 'border-l-red-500'
        if (isProcessing) return 'border-l-blue-500'
        return 'border-l-gray-300'
    }

    const getIconColor = () => {
        if (isApproved) return 'text-green-600'
        if (isRejected) return 'text-red-600'
        if (isProcessing) return 'text-blue-600'
        return 'text-gray-400'
    }

    const getStatusColor = () => {
        if (isApproved) return 'bg-green-100 text-green-800 border-green-300'
        if (isRejected) return 'bg-red-100 text-red-800 border-red-300'
        if (isProcessing) return 'bg-blue-100 text-blue-800 border-blue-300'
        return 'bg-gray-100 text-gray-800 border-gray-300'
    }

    return (
        <div
            className={`bg-white p-4 rounded-md border border-gray-200 border-l-4 ${getBorderColor()}`}
        >
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                    {isApproved ? (
                        <CheckCircle2 className={`w-5 h-5 ${getIconColor()}`} />
                    ) : isRejected ? (
                        <XCircle className={`w-5 h-5 ${getIconColor()}`} />
                    ) : (
                        <Clock className={`w-5 h-5 ${getIconColor()}`} />
                    )}
                    <div>
                        <p className="text-xs font-medium text-gray-500">
                            {t('approval.step')} {stepNumber}
                        </p>
                        <p className="font-semibold text-sm">
                            {step.approver_role}
                        </p>
                    </div>
                </div>
                <Badge
                    variant="outline"
                    className={`text-xs px-2 py-1 ${getStatusColor()}`}
                >
                    {isApproved && t('statuses.approved')}
                    {isRejected && t('statuses.rejected')}
                </Badge>
            </div>

            {/* Approver info */}
            {step.approver_name && (
                <div className="flex items-center gap-2 text-sm text-gray-700 mb-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{step.approver_name}</span>
                </div>
            )}

            {/* Timestamp */}
            {step.approved_at && (
                <div className="text-xs text-gray-500 mb-2">
                    {new Date(step.approved_at).toLocaleString()}
                </div>
            )}

            {/* Notes */}
            {step.notes && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                    <p className="text-sm text-gray-600 italic">
                        "{step.notes}"
                    </p>
                </div>
            )}

            {/* Action forms */}
            {isProcessing && (
                <div className="mt-3 pt-3 border-t border-gray-300 space-y-3">
                    <Textarea
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        placeholder={t('approval.reasonPlaceholder')}
                        className="w-full text-sm resize-none min-h-[80px]"
                        rows={3}
                        disabled={isApproving || isRejecting}
                    />
                    {feedback &&
                        feedback.trim().length > 0 &&
                        feedback.trim().length < 10 && (
                            <p className="text-xs text-red-600">
                                {t('approval.minReasonLength')}
                            </p>
                        )}
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            onClick={handleApprove}
                            disabled={isApproving || isRejecting}
                            className="flex-1"
                        >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            {isApproving
                                ? t('approval.approving')
                                : t('actions.approve')}
                        </Button>
                        <Button
                            size="sm"
                            variant="destructive"
                            onClick={handleReject}
                            disabled={
                                isApproving ||
                                isRejecting ||
                                !feedback ||
                                feedback.trim().length < 10
                            }
                            className="flex-1"
                        >
                            <XCircle className="w-4 h-4 mr-1" />
                            {isRejecting
                                ? t('approval.rejecting')
                                : t('actions.reject')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}

const ApprovalTab = ({ asset }: ApprovalTabProps) => {
    const { t } = useTranslation('asset')
    const steps = asset.approval_steps || []
    const request = asset.approval_request || null

    const handleSuccess = () => {
        // Refetch handled by mutation's reactQueryKeys
    }

    const approvedCount = steps.filter((s) => s.status === 'APPROVED').length
    const totalCount = steps.length

    return (
        <div className="space-y-2">
            {/* Status Summary Card */}
            <Card className="border-none">
                <CardHeader>
                    <CardTitle>
                        <Typogaphy className="font-semibold text-primary mb-2">
                            {t('approval.status')}
                        </Typogaphy>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="grid grid-cols-2 gap-2">
                            <span className="text-sm font-medium text-gray-500">
                                {t('approval.statusLabel')}
                            </span>
                            <span className="text-sm font-semibold">
                                {request?.approval_request_status
                                    ? t(
                                          `asset_statuses.${request.approval_request_status}`
                                      )
                                    : 'N/A'}
                            </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <span className="text-sm font-medium text-gray-500">
                                {t('approval.progress')}
                            </span>
                            <span className="text-sm font-semibold">
                                {approvedCount} / {totalCount}
                            </span>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Approval Steps */}
            <Card className="border-none">
                <CardHeader>
                    <CardTitle>
                        <Typogaphy className="font-semibold text-primary mb-2">
                            {t('approval.approvalSteps')}
                        </Typogaphy>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {steps.map((step, index) => (
                            <ApprovalStatusCard
                                requestStatus={
                                    asset.approval_request
                                        ?.approval_request_status ||
                                    StatusPending
                                }
                                key={step.approval_step_id}
                                step={step}
                                assetVersionId={
                                    asset.current_version.asset_version_id
                                }
                                onSuccess={handleSuccess}
                                t={t}
                                stepNumber={index + 1}
                            />
                        ))}
                    </div>
                    {steps.length === 0 && (
                        <div className="text-center text-gray-500 py-8">
                            {t('approval.noSteps')}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}

export default ApprovalTab
